/*
 * flashmeeting-be
 *
 * Author: Andgel BRISS
 * Copyright (c) 2025 Prodeo - All Rights Reserved
 */

import { SortArrowDownIcon } from '@common_components/icons/sortArrowDown.icon';
import { SortArrowUpIcon } from '@common_components/icons/sortArrowUp.icon';
import { NoDataAvailableIcon } from '@common_components/icons/noDataAvailable';
import Pagination from '@common_components/inputs/selects/pagination.component';
import { Loading } from '@common_components/loading.component';
import { ColumnCellProps, GenericTableProps, Theme } from 'interfaces/table.interface';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function ColumnCell<T extends Record<string, unknown>>( { row, col }: ColumnCellProps<T> ) {
  const { t } = useTranslation();
  const value = col.getValue ? col.getValue( row ) : row[ col.key as keyof T ];

  switch ( col.type ) {
  case 'boolean': {
    return (
      <td className={col.className || 'px-4 py-3'}>
        <span
          className={
            value
              ? 'bg-white text-primary-700 px-2 py-1 rounded-full text-xs font-bold'
              : 'bg-gray-50 text-gray-600 px-2 py-1 rounded-full text-xs'
          }
        >
          {value ? t( 'common:yes' ) : t( 'common:no' )}
        </span>
      </td>
    );
  }
  case 'theme': {
    const themeValue = value as Theme | undefined;
    return (
      <td className={col.className || 'px-4 py-3'}>
        <div className="flex items-center gap-4">
          {themeValue?.color && (
            <span
              className="inline-flex w-4 h-4 mt-1 rounded-full border border-gray-300 shadow p-0.5"
              style={{ backgroundColor: themeValue.color }}
              title={themeValue.color}
            />
          )}
          <span className="leading-5">
            {themeValue?.name || ''}
          </span>
        </div>
      </td>
    );
  }
  case 'number': {
    return (
      <td className={col.className || 'px-4 py-3'}>
        {typeof value === 'number' ? value : ' - '}
      </td>
    );
  }
  case 'actions': {
    return (
      <td className={col.className || 'px-4 py-3'}>
        {typeof value === 'function' ? value( row ) : value}
      </td>
    );
  }
  default: {
    return (
      <td className={col.className || 'px-4 py-3'}>
        {typeof value === 'object' &&
          value !== null &&
          !Array.isArray( value ) &&
          !React.isValidElement( value )
          ? JSON.stringify( value )
          : value !== undefined && value !== null
            ? String( value )
            : ' - '}
      </td>
    );
  }
  }
}

export function GenericTable<T extends Record<string, unknown>>( {
  getData,
  renderList,
  columns,
  title,
  onRowClick,
  headerRight,
  rowsPerPage = 30,
  defaultSortKey,
  defaultSortOrder = 'asc',
}: GenericTableProps<T> ) {
  const { t } = useTranslation();

  const [ response, setResponse ] = useState<{ total: number, datas: T[] } | undefined>();
  const [ sortKey, setSortKey ] = useState<string | null>( defaultSortKey ?? null );
  const [ sortOrder, setSortOrder ] = useState<'asc' | 'desc'>( defaultSortOrder );
  const [ pageActual, setPageActual ] = useState<number>( 1 );
  const [ totalPages, setTotalPages ] = useState<number>( 0 );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );
  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  const handleSort = ( key: string ) => {
    if ( sortKey === key ) {
      setSortOrder( sortOrder === 'asc' ? 'desc' : 'asc' );
    } else {
      setSortKey( key );
      setSortOrder( 'asc' );
    }
  };

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshDatas();
    }
  }, [ isFirstRender, sortKey, sortOrder, pageActual, rowsPerPage ] );

  // Quand la liste doit être rechargée (changement de filtres, etc.),
  // on revient systématiquement à la première page pour éviter de rester
  // sur une page qui n'existe plus.
  useEffect( () => {
    if ( !isFirstRender ) {
      setPageActual( prevPage => {
        if ( prevPage === 1 ) {
          void refreshDatas();
          return prevPage;
        }
        return 1;
      } );
    }
  }, [ renderList ] );

  useEffect( () => {
    if ( sortKey != null && sortOrder != null ) {
      setIsFirstRender( false );
    }
  }, [ sortKey, sortOrder ] );

  useEffect( () => {
    if ( sortKey == null && columns[ 0 ].key ) {
      handleSort( columns[ 0 ].key.toString() );
    }
  }, [ columns[ 0 ].key, sortKey ] );

  const refreshDatas = async () => {
    if ( sortKey ) {
      setIsLoading( true );
      const result = await getData( pageActual, rowsPerPage, { key: sortKey, order: sortOrder } );
      setResponse( result );
      setTotalPages( Math.ceil( result.total / rowsPerPage ) );
      setIsLoading( false );
    }
  };

  return (
    <>
      {
        isLoading ?
          <div className="w-full flex justify-center items-center">
            <Loading size={64} />
          </div>
          :
          response && response.datas.length > 0 ?
            <div className="flex flex-col w-full max-w-full">
              <section className="mb-3 pl-3 flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-3 pr-3">
                  <div className="flex items-center gap-3">
                    <h2 className="font-comfortaa text-gray-500">{title}</h2>
                    <span className="text-black px-3 py-1 text-sm">
                      {response.total} {t( 'common:elements' )}
                    </span>
                  </div>
                  {headerRight && (
                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full sm:w-auto">
                      {headerRight}
                    </div>
                  )}
                </div>
              </section>

              <div className="w-full rounded-xl border-gray-200 border overflow-hidden" style={{ maxWidth: '100%' }}>
                <div className="w-full overflow-x-auto">
                  <div className="w-full max-h-[70vh] overflow-y-auto">
                    <table className="min-w-full table-auto text-base text-left">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr>
                          {columns.map( col => {return (
                            <th
                              key={col.key as string}
                              className="px-4 py-2 relative border-b-2 border-gray-300 text-left last:rounded-tr-xl first:rounded-tl-xl text-gray-600 font-normal tracking-wider transition-colors duration-300 hover:bg-gray-100 cursor-pointer select-none"
                              onClick={() => {return handleSort( col.key as string );}}
                            >
                              {col.label}
                              {sortKey === col.key && (
                                <span className="absolute flex-center top-0 right-2 h-full flex-center">
                                  {sortOrder === 'asc' ? <SortArrowDownIcon size={5} /> : <SortArrowUpIcon size={5} />}
                                </span>
                              )}
                            </th>
                          );} )}
                        </tr>
                      </thead>

                      <tbody>
                        {response.datas.length === 0 ? (
                          <tr>
                            <td colSpan={columns.length} className="text-center py-8 text-gray-400 italic bg-white rounded-xl">
                              {t( 'common:no_data' )}
                            </td>
                          </tr>
                        ) : (
                          response.datas.map( ( row: T, rowIndex: number ) => {
                            const isLastRow = rowIndex === response.datas.length - 1;

                            const isClickable = typeof onRowClick === 'function';

                            return (
                              <tr
                                key={ String( ( row as { id?: string | number; key?: string | number } ).id ) + String( ( row as { key?: string | number } ).key ?? rowIndex ) }
                                className={`transition-colors duration-200 border-b last:border-none ${
                                  ( row.disable == 1  ? 'bg-red-100' : ( rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50' ) )
                                } ${
                                  isClickable && ( row as unknown as { isUserOnTable?: boolean } ).isUserOnTable !== false
                                    ? 'cursor-pointer hover:brightness-95'
                                    : 'cursor-default'
                                }`}
                                onClick={
                                  isClickable && ( row as unknown as { isUserOnTable?: boolean } ).isUserOnTable !== false
                                    ? () => {return onRowClick( row );}
                                    : undefined
                                }
                              >
                                {columns.map( ( col, colIndex ) => {
                                  const value = col.getValue ? col.getValue( row ) : row[ col.key as keyof T ];
                                  const isEmpty =
                                  value === undefined ||
                                  value === null ||
                                  ( typeof value === 'string' && value.trim() === '' );
                                  const baseClassName = `${col.className || 'px-4 py-3'}`;
                                  const extraClassName =
                                  colIndex === 0 && `${isLastRow ? 'first:rounded-bl-xl last:rounded-br-xl' : ''}`;

                                  return isEmpty && col.type !== 'actions' ? (
                                    <td key={col.key as string} className={`${baseClassName} ${extraClassName} text-center text-gray-400`}>
                                    -
                                    </td>
                                  ) : (
                                    <ColumnCell
                                      key={col.key as string}
                                      row={row}
                                      col={{
                                        ...col,
                                        className: `${baseClassName} ${extraClassName}`.trim(),
                                      }}
                                    />
                                  );
                                } )}
                              </tr>
                            );
                          } )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {
                totalPages > 1 && (
                  <div className="w-full max-w-full overflow-x-hidden flex justify-center mt-4 mb-10">
                    <Pagination
                      currentPage={pageActual}
                      totalPages={totalPages}
                      onPageChange={setPageActual}
                    />
                  </div>
                )
              }
            </div>
            :
            <div className="w-full flex flex-col items-center text-gray-600 text-center italic">
              <NoDataAvailableIcon size={10} className="text-gray-400 mb-4" />
              <p>{t( 'common:no_data_to_display' )}</p>
            </div>
      }
    </>
  );
}
